import { createContext, useContext } from "react";
import type { EntityTypeDetailsPatch } from "@/features/entity-types/components/entity-type-details-panel";
import type { AssetTypeDisplay } from "@/services/asset/asset.types";
import type { AssetTypeScope } from "./asset-type-detail.utils";

export type DrawerMode = "view" | "edit";

export type AssetTypeActiveTab =
	| "details"
	| "access"
	| "schema"
	| "form"
	| "lifecycle"
	| "rules"
	| "activity";

export type AssetTypeDrawerContextValue = {
	scope: AssetTypeScope;
	assetType: AssetTypeDisplay;
	mode: DrawerMode;
	setMode: (mode: DrawerMode) => void;
	activeTab: AssetTypeActiveTab;
	canEdit: boolean;
	draftName: string;
	draftDescription: string;
	draftIcon: string;
	setDraftName: (value: string) => void;
	setDraftDescription: (value: string) => void;
	setDraftIcon: (value: string) => void;
	/** null = org-wide access (no sub-orgs); [] = none selected */
	draftSubOrgIds: string[] | null;
	setDraftSubOrgIds: (next: string[] | null) => void;
	isSaving: boolean;
	isValid: boolean;
	isDirty: boolean;
	saveDraft: () => Promise<void>;
	cancelDraft: () => void;
	/** Inline Details save (name / description / icon) merged over current values. */
	persistDetails: (patch: EntityTypeDetailsPatch) => Promise<void>;
	/** Latest Required Location flag included on every type PUT. */
	locationRequired: boolean;
	persistLocationRequired: (next: boolean) => Promise<void>;
};

export const AssetTypeDrawerContext =
	createContext<AssetTypeDrawerContextValue | null>(null);

export function useAssetTypeDrawerContext(): AssetTypeDrawerContextValue {
	const ctx = useContext(AssetTypeDrawerContext);
	if (!ctx) {
		throw new Error("Asset type drawer context is not available.");
	}
	return ctx;
}
